/**
 * Goal Detail Sheet — full-screen detail view for a single goal.
 * Slides in from right (habit-profile-sheet pattern).
 * Shows: hero, description, roadmap, progress, linked habits, wishes.
 */

import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft, Lock, Globe, Link2, Heart, Send, Trash2,
  CalendarDays, TrendingUp,
} from 'lucide-react'
import { format } from 'date-fns'
import { useAuthStore } from '@turnifly/shared/stores'
import { useGoal, useGoalEntries, useGoalWishes, useCreateWish, useDeleteWish } from '@turnifly/shared/hooks'
import { useCategories } from '@turnifly/shared/hooks'
import { useGoalsStore } from '@turnifly/shared/stores'
import { calculateProgress, getProgressColor, getProgressBarVariant } from '@turnifly/shared/utils'
import { ProgressBar } from '@turnifly/shared/ui'
import { Skeleton } from '@turnifly/shared/ui'
import { GoalRoadmap } from './goal-roadmap'
import { GoalEntriesList } from './goal-entries-list'
import type { Goal } from '@turnifly/shared/types'

interface GoalDetailSheetProps {
  goalId: string
  onClose: () => void
}

export const GoalDetailSheet: React.FC<GoalDetailSheetProps> = ({ goalId, onClose }) => {
  const { t } = useTranslation()
  const { data: goal, isLoading } = useGoal(goalId)

  return (
    <AnimatePresence>
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 220 }}
        className="fixed inset-0 z-50 bg-[var(--color-bg)] overflow-y-auto"
      >
        {/* Fixed header */}
        <div className="fixed top-14 left-0 right-0 z-10 bg-[var(--color-bg)]/90 backdrop-blur-sm">
          <div className="max-w-[600px] mx-auto flex items-center h-[52px] px-4">
            <button onClick={onClose} className="p-2 -ml-2 rounded-xl hover:bg-white/[0.06] tap-highlight-transparent">
              <ArrowLeft size={20} className="text-text" />
            </button>
            <h2 className="ml-2 text-[15px] font-display font-bold text-text truncate">
              {goal?.name ?? t('goals.title')}
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-[600px] mx-auto px-4 pt-[125px] pb-24">
          {isLoading ? (
            <DetailSkeleton />
          ) : goal ? (
            <DetailContent goal={goal} onClose={onClose} />
          ) : null}
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

// ── Skeleton ──

const DetailSkeleton: React.FC = () => (
  <div className="space-y-4">
    <div className="flex flex-col items-center gap-3">
      <Skeleton className="w-16 h-16 rounded-2xl" />
      <Skeleton className="w-48 h-5 rounded" />
      <Skeleton className="w-32 h-4 rounded" />
    </div>
    <Skeleton className="h-20 rounded-2xl" />
    <Skeleton className="h-32 rounded-2xl" />
  </div>
)

// ── Content ──

const DetailContent: React.FC<{ goal: Goal; onClose: () => void }> = ({ goal }) => {
  const { t, i18n } = useTranslation()
  const userId = useAuthStore((s) => s.user?.id) ?? ''
  const isOwner = goal.user_id === userId
  const { data: entries } = useGoalEntries(goal.id)
  const { data: categories } = useCategories()
  const openEntrySheet = useGoalsStore((s) => s.openEntrySheet)
  const isSteps = goal.goal_type === 'steps'

  const categoryLabel = (() => {
    if (!goal.category) return null
    const lang = i18n.language === 'uz' ? 'name_uz' : 'name_en'
    const cats = (categories ?? []) as Array<Record<string, string>>
    const cat = cats.find((c) => c.id === goal.category)
    return cat ? (cat[lang] ?? cat['name_en'] ?? goal.category) : goal.category
  })()

  const progress = calculateProgress(goal)
  const progressColor = getProgressColor(progress)
  const barVariant = getProgressBarVariant(progress)
  const isAchieved = goal.status === 'achieved'

  const daysElapsed = Math.ceil(
    (Date.now() - new Date(goal.created_at).getTime()) / (1000 * 60 * 60 * 24),
  )

  return (
    <div className="space-y-4">
      {/* Hero */}
      <div className="flex flex-col items-center text-center">
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl mb-3 ${
          isAchieved ? 'bg-success/[0.1]' : 'bg-white/[0.06]'
        }`}>
          {goal.icon}
        </div>
        <h1 className="text-lg font-display font-bold text-text">{goal.name}</h1>
        <div className="flex items-center gap-2 mt-1">
          {categoryLabel && (
            <span className="text-xs text-text-hint bg-white/[0.06] px-2 py-0.5 rounded-full">{categoryLabel}</span>
          )}
          <span className="text-xs text-text-hint">{goal.year}</span>
          {goal.is_private
            ? <Lock size={12} className="text-text-hint" />
            : <Globe size={12} className="text-text-hint/50" />
          }
        </div>
      </div>

      {/* Description */}
      {goal.description && (
        <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
          <p className="text-sm text-text/80 leading-relaxed">{goal.description}</p>
        </div>
      )}

      {/* Progress */}
      <div className={`rounded-2xl p-4 ${
        isAchieved
          ? 'bg-success/[0.04] border border-success/[0.15]'
          : 'bg-white/[0.03] border border-white/[0.06]'
      }`}>
        <div className="flex items-baseline justify-between mb-2">
          <span className={`text-sm font-semibold tabular-nums ${progressColor}`}>
            {`${goal.current_value}/${goal.target_value} ${goal.unit} (${progress}%)`}
          </span>
          {isAchieved && <span className="text-xs text-success font-medium">{t('goals.status.achieved')}</span>}
        </div>
        <ProgressBar value={progress} variant={barVariant} />
      </div>

      {/* Roadmap */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
        <h3 className="text-xs font-semibold text-text-hint mb-2">{t('goals.roadmap')}</h3>
        <GoalRoadmap goal={goal} />
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-2">
        <StatCell icon={<CalendarDays size={14} className="text-primary" />} value={`${daysElapsed}`} label={t('goals.daysElapsed')} />
        <StatCell icon={<TrendingUp size={14} className="text-success" />} value={`${progress}%`} label={t('goals.progress').split('(')[0]} />
        <StatCell icon={<Link2 size={14} className="text-accent" />} value={`${entries?.length ?? 0}`} label={t('goals.entries')} />
      </div>

      {/* Entries */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-text-hint">
            {isSteps ? t('goals.stepsLabel') : t('goals.entries')}
          </h3>
          {isOwner && !isSteps && !isAchieved && (
            <button
              onClick={() => openEntrySheet(goal.id)}
              className="text-[10px] text-primary font-medium px-2 py-1 rounded-lg bg-primary/[0.08] hover:bg-primary/[0.12] tap-highlight-transparent"
            >
              + {t('goals.addEntry')}
            </button>
          )}
        </div>
        {entries ? (
          <GoalEntriesList
            goal={goal}
            entries={entries}
            isOwner={isOwner}
            showAll
          />
        ) : (
          <Skeleton className="h-16 rounded-xl" />
        )}
      </div>

      {/* Wishes — only for non-owners on public goals */}
      {!isOwner && !goal.is_private && (
        <WishesSection goalId={goal.id} />
      )}

      {/* Wishes received — for owners */}
      {isOwner && (
        <WishesSection goalId={goal.id} readOnly />
      )}

      {/* Footer */}
      <p className="text-[11px] text-text-hint/40 text-center">
        {t('goals.createdDate', { date: format(new Date(goal.created_at), 'dd.MM.yyyy') })}
      </p>
    </div>
  )
}

// ── Stat Cell ──

const StatCell: React.FC<{ icon: React.ReactNode; value: string; label: string }> = ({ icon, value, label }) => (
  <div className="flex flex-col items-center py-3 px-2 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
    <div className="mb-1">{icon}</div>
    <span className="text-base font-display font-bold text-text">{value}</span>
    <span className="text-[9px] text-text-hint text-center leading-tight">{label}</span>
  </div>
)

// ── Wishes Section ──

const WishesSection: React.FC<{ goalId: string; readOnly?: boolean }> = ({ goalId, readOnly }) => {
  const { t } = useTranslation()
  const userId = useAuthStore((s) => s.user?.id) ?? ''
  const { data: wishes, isLoading } = useGoalWishes(goalId)
  const createWish = useCreateWish()
  const deleteWish = useDeleteWish()
  const [text, setText] = useState('')

  const handleSend = useCallback((): void => {
    if (!text.trim()) return
    createWish.mutate({ goalId, text: text.trim() })
    setText('')
  }, [text, goalId, createWish])

  const hasMyWish = wishes?.some((w) => w.user_id === userId)

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
      <div className="flex items-center gap-2 mb-3">
        <Heart size={14} className="text-danger" />
        <h3 className="text-xs font-semibold text-text-hint">
          {t('goals.wishes')} {wishes && wishes.length > 0 ? `(${wishes.length})` : ''}
        </h3>
      </div>

      {/* Wish list */}
      {isLoading ? (
        <Skeleton className="h-10 rounded-xl" />
      ) : wishes && wishes.length > 0 ? (
        <div className="space-y-1.5 mb-3">
          {wishes.map((wish) => (
            <div key={wish.id} className="flex items-start gap-2 px-2.5 py-2 rounded-xl bg-white/[0.02]">
              <div className="w-5 h-5 rounded-full bg-white/[0.08] flex items-center justify-center text-[10px] shrink-0 mt-0.5">
                {wish.profile?.full_name?.charAt(0) ?? '?'}
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-[10px] text-text-hint">{wish.profile?.full_name ?? ''}</span>
                <p className="text-xs text-text">{wish.text}</p>
              </div>
              {wish.user_id === userId && (
                <button
                  onClick={() => deleteWish.mutate({ wishId: wish.id, goalId })}
                  className="p-1 rounded hover:bg-white/[0.06] shrink-0"
                >
                  <Trash2 size={12} className="text-text-hint/50" />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-text-hint/50 mb-3">{t('goals.noWishes')}</p>
      )}

      {/* Wish input — only if not read-only and haven't wished yet */}
      {!readOnly && !hasMyWish && (
        <div className="flex items-center gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('goals.wishPlaceholder')}
            maxLength={200}
            className="flex-1 h-9 px-3 rounded-xl bg-white/[0.06] border border-white/[0.08] text-xs text-text placeholder:text-text-hint/40"
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          />
          <button
            onClick={handleSend}
            disabled={!text.trim() || createWish.isPending}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-primary/[0.12] text-primary disabled:opacity-30 tap-highlight-transparent"
          >
            <Send size={14} />
          </button>
        </div>
      )}
    </div>
  )
}

